import React from 'react';
import { useHistory } from 'react-router-dom';
import { WeezHeader, Card, Modal, Button } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import Distributor from '../../../models/distributor';
import { useForm } from '@weezevent/weezjs-core';
import UploadRequestConfigModel from '../../../models/UploadRequestConfig';

import { DataStep, FileStep, RatesStep, AnalysisStep, DistributorsForm } from './steps';

const defaultOrigin = 1173;

export default function AttendeeImport({ organization, event }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [newFileModal, setNewFileModal] = React.useState(false);

    const [form, setForm, setFormObject] = useForm(
        {
            origin: defaultOrigin, // Default distributor
            newOrigin: null,
            file: [],
            importRequest: null,
            requestDatas: {},
            eventRates: []
        },
        [defaultOrigin]
    );

    const [uploadSuccess, setUploadSuccess] = React.useState(form?.importRequest ? true : false);

    const [distributors, loadingDistributors, syncDistributor] = Distributor.useApiModel({
        cache: false,
        organizationId: organization.pk
    });

    const requestPayload = React.useMemo(() => {
        const datas = {
            event_id: event.pk
        };
        if (!form.newOrigin) {
            datas['distributor_id'] = form.origin;
        } else {
            datas['distributor_name'] = form.newOrigin;
        }
        return datas;
    }, [form, event]);

    const disabled = React.useMemo(() => event.use_sessions || event.is_seated || event.isFakeSeatingMultipleInventories(), [event]);

    return (
        <>
            <WeezHeader title={t('ticket.attendees.import.title')} backLink={{ onClick: () => history.push(`/ticket/O${organization.id}/E${event.id}/attendees/imports`) }} />
            {newFileModal && (
                <Modal
                    title={t('ticket.attendees.imports.modal.title')}
                    open={newFileModal}
                    onClose={() => setNewFileModal(false)}
                    submitButton={
                        <Button color="red" labelPostion="left" onClick={() => window.location.reload()}>
                            {t('ticket.common.cta.confirm')}
                        </Button>
                    }
                    cancelButton={<Button onClick={() => setNewFileModal(false)}>{t(`ticket.common.action.back`)}</Button>}
                >
                    {t('ticket.attendees.imports.modal.body')}
                </Modal>
            )}
            <Card.FormManager labelPreviousStepMobile={t('ticket.common.back_step')} labelStepMobile={t(['ticket.common.step.current'])} withStepsMobile>
                <Card.FormStep
                    editLabel={t('ticket.common.action.edit')}
                    title={`1 - ${t('ticket.attendees.imports.steps.file.title')}`}
                    subtitle={t('ticket.attendees.imports.steps.file.subtitle')}
                    summary={`${t('ticket.attendees.imports.steps.file.summary')}: ${form.file?.path}`}
                >
                    <FileStep
                        uploadModel={UploadRequestConfigModel}
                        onDone={onCardDone => {
                            onCardDone();
                        }}
                        event={event}
                        blockContinue={!form.origin || (form.origin === 'newOrigin' && !form.newOrigin)}
                        disabled={disabled}
                        fileName={event.is_seated ? 'template_import_with_seat.xlsx' : 'template_import_no_seat.xlsx'}
                        organization={organization}
                        requestPayload={requestPayload}
                        defaultFormData={{ origin: defaultOrigin, newOrigin: '' }}
                        distributors={distributors}
                        loadingDistributors={loadingDistributors}
                        setUploadSuccess={setUploadSuccess}
                        onDelete={syncDistributor}
                        form={form}
                        setFormObject={setFormObject}
                        setForm={setForm}
                        syncDistributor={syncDistributor}
                    >
                        <DistributorsForm
                            displayErrorMessage={disabled}
                            disabledSelect={disabled || uploadSuccess}
                            defaultOrigin={defaultOrigin}
                            distributors={distributors}
                            loadingDistributors={loadingDistributors}
                            form={form}
                            setForm={setForm}
                        />
                    </FileStep>
                </Card.FormStep>
                <Card.FormStep
                    editLabel={t('ticket.common.action.edit')}
                    title={`2 - ${t('ticket.attendees.imports.steps.data.title')}`}
                    subtitle={t('ticket.attendees.imports.steps.data.subtitle')}
                    // Here we filter columns based on their actual mapping, and decrease 1 item since rate id is automatically added
                    summary={t('ticket.attendees.imports.steps.data.summary', { count: form.requestDatas?._details?.columns?.filter(cl => cl.target).length - 1 })}
                >
                    <DataStep
                        event={event}
                        form={form}
                        setForm={setForm}
                        openModal={() => setNewFileModal(true)}
                        onDone={(requestDatas, onCardDone) => {
                            setForm('requestDatas', requestDatas);
                            onCardDone();
                        }}
                    />
                </Card.FormStep>
                <Card.FormStep
                    editLabel={t('ticket.common.action.edit')}
                    title={`3 - ${t('ticket.attendees.imports.steps.rates.title')}`}
                    subtitle={t('ticket.attendees.imports.steps.rates.subtitle')}
                    summary={`${t('ticket.attendees.imports.steps.rates.summary', { count: form.requestDatas?._data?.rates_details.length })}`}
                >
                    <RatesStep
                        event={event}
                        organization={organization}
                        form={form}
                        setForm={setForm}
                        openModal={() => setNewFileModal(true)}
                        onDone={(requestDatas, onCardDone) => {
                            setForm('requestDatas', requestDatas);
                            onCardDone();
                        }}
                    />
                </Card.FormStep>
                <Card.FormStep
                    editLabel={t('ticket.common.action.edit')}
                    title={`4 - ${t('ticket.attendees.imports.steps.analysis.title')}`}
                    subtitle={t('ticket.attendees.imports.steps.analysis.subtitle')}
                >
                    <AnalysisStep
                        redirectUrl={`/ticket/O${organization.id}/E${event.id}/attendees/imports`}
                        event={event}
                        organization={organization}
                        form={form}
                        setForm={setForm}
                        setFormObject={setFormObject}
                        openModal={() => setNewFileModal(true)}
                        onDone={(requestDatas, onCardDone) => {
                            setForm('requestDatas', requestDatas);
                            onCardDone();
                        }}
                    />
                </Card.FormStep>
            </Card.FormManager>
        </>
    );
}
